import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { AdmissionRequirementValue } from './AdmissionRequirement';
import type { DeclarationValues } from '..';
import {
  type FundingDeclaration,
  type FundingPanel,
  type FundingPlan,
  type FundingSource,
  type FundingSourceConfiguration,
  type FundingsValue,
} from '../../components';
import { MappingType } from '../../components/constant';
import type { SelectOption } from '../../../../components';
import { checkInvalidDate } from '../../../Application/graphql';
import { type SupervisorFormData } from '../../../Monitor/graphql';
import { type FUNDING_STATUS } from '../../../ScholarshipManagement';

const GET_NOMINATIONS = gql`
  query ($id: ID!) {
    programmeChoiceMetadata {
      departments {
        value
        label
      }
    }

    getFundingSourceConfiguration {
      mapping
      validTags
    }

    nomination(id: $id) {
      admission {
        programme
        requirements {
          generalEntranceRequirements {
            requirements
            justification
          }
          englishLanguageRequirements {
            requirements
          }
          justification
        }
      }
      supervisors {
        type
        netId
        institution
        title
        jobTitle
        givenName
        surname
        department
        email
        previousSuccessfulSupervision
        isDrcChair
        isDoRPgS
        confirmedAsCoSupervisor
      }
      scholarshipNomination {
        subjectClassification
        competitiveStudentship
      }
      fundings {
        formFinished
        supportYears
        status
        schema
        source
        stipendBudgetCreationConfirm
        accountInfoAccurateConfirm
        externalGrantsConfirm
        sufficientFundingConfirm
        fundingPlan {
          accountCode
          period {
            from
            to
          }
        }
        requiredDocuments {
          id
          name
          link
        }
        projectInformation {
          role
          PIName
          PIDepartment
          declarations {
            content
            confirmed
            accountCode
          }
          justification
        }
        proofOfScheme {
          id
          link
          name
        }
        declaration {
          declaration
          confirmed
        }
      }

      declarations {
        content
        confirmed
        justification
      }
      remarks
      submission {
        submitted
      }
    }
  }
`;

type GetNominationBatch = {
  admission: AdmissionRequirementValue;
  supervisorTeam: SupervisorFormData[];
  allFundingSourceConfiguration: FundingSourceConfiguration;
  fundings: FundingsValue[];
  declarations: DeclarationValues[];
  remarks: string[];
  departmentOptions: SelectOption[];
  subjectClassification: string;
  competitiveStudentship: number;
  loading: boolean;
  error: ApolloError | undefined;
  hasSubmitted: boolean;
  refetch: () => void;
};

const NA = 'N/A';

export const useGetNominationBatch = (id: string): GetNominationBatch => {
  const { data, loading, error, refetch } = useQuery(GET_NOMINATIONS, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'no-cache',
  });

  // programmeChoiceMetadata, department
  const departmentOptions: SelectOption[] = (
    (data?.programmeChoiceMetadata?.departments as any[]) ?? []
  ).map(department => ({
    value: department.value,
    label: department.label,
  }));

  // admission
  let admission: AdmissionRequirementValue = {
    programme: '',
    entranceRequirement: '',
    entranceJustification: '',
    languageRequirement: '',
    justification: '',
  };

  // fundingSourceConfiguration (full version)
  const allFundingSourceConfiguration: FundingSourceConfiguration = {
    mapping: {
      COMPETITIVE:
        data?.getFundingSourceConfiguration?.mapping?.COMPETITIVE ?? [],
      NON_COMPETITIVE:
        data?.getFundingSourceConfiguration?.mapping?.NON_COMPETITIVE ?? [],
      OTHER: data?.getFundingSourceConfiguration?.mapping?.OTHER ?? [],
    },
    validTags: data?.getFundingSourceConfiguration?.validTags ?? [],
  };

  if (
    data?.nomination?.admission !== undefined &&
    data?.nomination?.admission !== null
  ) {
    const {
      programme,
      requirements: {
        generalEntranceRequirements: [
          {
            requirements: entranceRequirement,
            justification: entranceJustification,
          },
        ],
        englishLanguageRequirements: [{ requirements: languageRequirement }],
        justification,
      },
    } = data?.nomination?.admission ?? {};

    admission = {
      programme,
      entranceRequirement,
      entranceJustification,
      languageRequirement,
      justification,
    };
  }

  // supervisor team
  const supervisorTeam: SupervisorFormData[] =
    data?.nomination?.supervisors ?? [];

  // funding source
  const { nomination } = data ?? {};
  const { fundings: rawFundings } = nomination ?? {};
  // put not competitive schema first to make it display the latest funding
  rawFundings?.sort((a: any, b: any) => {
    if (a.schema !== MappingType.COMPETITIVE) {
      return -1;
    }
    if (b.schema !== MappingType.COMPETITIVE) {
      return 1;
    }
    return 0;
  });
  const fundings: FundingsValue[] =
    (rawFundings ?? [])?.map((fund: any) => {
      const transformedSources = (fund?.source as FundingSource) ?? '';
      const transformedFundingPlan: FundingPlan[] = (
        fund?.fundingPlan ?? []
      ).map((plan: FundingPlan) => ({
        accountCode: plan?.accountCode,
        period: {
          from: checkInvalidDate(plan.period.from) ? null : plan.period.from,
          to: checkInvalidDate(plan.period.to) ? null : plan.period.to,
        },
      }));
      const transformedProjectInformation = fund?.projectInformation ?? {};

      return {
        schema: fund?.schema ?? '',
        sufficientFundingConfirm: fund?.sufficientFundingConfirm ?? false,
        stipendBudgetCreationConfirm:
          fund?.stipendBudgetCreationConfirm ?? false,
        accountInfoAccurateConfirm: fund?.accountInfoAccurateConfirm ?? false,
        externalGrantsConfirm: fund?.externalGrantsConfirm ?? false,
        source: transformedSources,
        fundingPlan: transformedFundingPlan,
        requiredDocuments: fund?.requiredDocuments ?? [],
        projectInformation: transformedProjectInformation,
        proofOfScheme: fund?.proofOfScheme ?? [],
        declaration: (fund?.declaration as FundingDeclaration[]) ?? [],
        formFinished: fund?.formFinished ?? false,
        supportYears: fund?.supportYears ?? 0,
        status: fund?.status as FUNDING_STATUS,
      };
    }) ?? [];

  // declarations
  const declarations: DeclarationValues[] =
    data?.nomination?.declarations ?? [];
  // remarks
  const remarks: string[] = data?.nomination?.remarks ?? [''];
  const hasSubmitted = data?.nomination?.submission?.submitted;

  const scholarshipNominationRawData =
    data?.nomination?.scholarshipNomination ?? {};

  const subjectClassification =
    (scholarshipNominationRawData?.subjectClassification as FundingPanel) ?? NA;

  const competitiveStudentship: number | null =
    (scholarshipNominationRawData?.competitiveStudentship as number) ?? null;
  return {
    admission,
    supervisorTeam,
    fundings,
    declarations,
    remarks,
    departmentOptions,
    hasSubmitted,
    allFundingSourceConfiguration,
    subjectClassification,
    competitiveStudentship,
    loading,
    error,
    refetch,
  };
};
